import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useUser } from "@/context/useUser";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Loader2, ArrowRight, Mail, Lock, KeyRound } from "lucide-react";
import logo from "@project/assets/jivandhara-logo.png";
import templeBg from "@project/assets/admin-temple-bg.webp";
import { getApiErrorMessage } from "@project/lib/api-error";
import { projectToast } from "@project/lib/toast";
import { initializeProjectTenant } from "@project/services";
import AuthService from "@/services/APIs/AuthService";

export default function LoginPage() {
  const nav = useNavigate();
  const { login, isAuthenticated } = useUser();
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    initializeProjectTenant();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      nav({ to: "/admin/dashboard" });
    }
  }, [isAuthenticated, nav]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      projectToast.error("Enter both email and password");
      return;
    }
    setBusy(true);
    try {
      await login(email.trim(), password);
      projectToast.success("Signed in successfully");
      nav({ to: "/admin/dashboard" });
    } catch (error) {
      projectToast.error(
        getApiErrorMessage(error, "Invalid email or password")
      );
    } finally {
      setBusy(false);
    }
  };

  const handleForgot = async () => {
    if (!email.trim()) {
      projectToast.info(
        "Enter your email above first, then click Forgot Password."
      );
      return;
    }
    setBusy(true);
    try {
      await AuthService.forgotPassword(email.trim());
      projectToast.success("Password reset link sent to your email");
    } catch (error) {
      projectToast.error(
        getApiErrorMessage(error, "Could not send reset link")
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden p-4">
      <img
        src={templeBg}
        alt=""
        aria-hidden
        className="absolute inset-0 -z-10 h-full w-full object-cover"
      />
      <div className="from-background/40 via-background/30 to-background/70 absolute inset-0 -z-10 bg-gradient-to-b backdrop-blur-sm" />

      <div className="bg-card/95 shadow-foreground/20 w-full max-w-md rounded-3xl border border-white/40 p-8 shadow-2xl backdrop-blur-md sm:p-10">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="ring-gold/40 mb-4 flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl bg-secondary shadow-sm ring-2">
            <img
              src={logo}
              alt="Jivandhara logo"
              className="h-16 w-16 object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-foreground">Admin Login</h1>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground">
            Sign in with your Architecture One account to access the admin
            panel.
          </p>
        </div>

        <form
          onSubmit={handleSignIn}
          className="bg-background/60 space-y-3 rounded-2xl border border-border p-5"
        >
          <div>
            <Label htmlFor="email" className="mb-1 block text-xs">
              Email Address
            </Label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="pl-8"
              />
            </div>
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <Label htmlFor="password" className="text-xs">
                Password
              </Label>
              <button
                type="button"
                onClick={handleForgot}
                disabled={busy}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline disabled:opacity-50"
              >
                <KeyRound className="h-3 w-3" /> Forgot Password?
              </button>
            </div>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-8"
              />
            </div>
          </div>
          <Button type="submit" disabled={busy} className="w-full">
            {busy ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <ArrowRight className="mr-1 h-4 w-4" />
            )}
            Sign In
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/"
            className="text-xs text-muted-foreground hover:text-primary"
          >
            ← Back to website
          </Link>
        </div>
      </div>
    </div>
  );
}
